import React from 'react';
import { Sparkles } from 'lucide-react';

/**
 * Full-page aesthetic loader for the AI Creations (kits) list initial fetch.
 */
const AiCreationsLoader = () => {
  return (
    <div
      className="relative min-h-[52vh] sm:min-h-[58vh] flex flex-col items-center overflow-hidden rounded-2xl sm:rounded-3xl border border-violet-100/60 bg-gradient-to-b from-white via-violet-50/40 to-indigo-50/50 shadow-inner"
      aria-busy="true"
      aria-label="Loading AI creations"
    >
      {/* Ambient orbs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl sm:rounded-3xl">
        <div className="absolute -top-20 -left-16 h-56 w-56 rounded-full bg-violet-300/35 blur-3xl animate-blob" />
        <div className="absolute top-1/4 -right-12 h-48 w-48 rounded-full bg-fuchsia-300/30 blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute bottom-8 left-1/4 h-64 w-64 rounded-full bg-indigo-300/25 blur-3xl animate-blob animation-delay-4000" />
      </div>

      {/* Hero */}
      <div className="relative z-10 flex flex-col items-center px-4 pt-8 pb-6 sm:pt-10 sm:pb-8 text-center">
        <div className="relative mb-6 sm:mb-7">
          <div
            className="absolute inset-0 scale-125 rounded-3xl bg-gradient-to-tr from-violet-500/30 via-fuchsia-500/25 to-indigo-500/30 blur-2xl motion-reduce:animate-none animate-pulse"
            aria-hidden
          />
          <div
            className="relative flex h-[5.5rem] w-[5.5rem] sm:h-24 sm:w-24 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-indigo-600 p-[3px] shadow-xl motion-reduce:animate-none animate-[spin_10s_linear_infinite]"
            aria-hidden
          >
            <div className="flex h-full w-full items-center justify-center rounded-[13px] bg-white/95 shadow-inner backdrop-blur-sm">
              <Sparkles
                className="h-10 w-10 sm:h-11 sm:w-11 text-violet-600 motion-reduce:animate-none animate-pulse"
                strokeWidth={1.35}
              />
            </div>
          </div>
        </div>

        <h2 className="text-xl sm:text-2xl font-bold tracking-tight bg-gradient-to-r from-violet-800 via-fuchsia-700 to-indigo-700 bg-clip-text text-transparent">
          Curating your AI creations
        </h2>
        <p className="mt-2 max-w-md text-sm sm:text-base text-slate-600 leading-relaxed">
          Pulling fabrics, palettes, and studio magic into view—almost there.
        </p>

        <div className="mt-5 flex items-center gap-2" aria-hidden>
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 motion-reduce:animate-none animate-pulse opacity-60"
              style={{ animationDelay: `${i * 120}ms` }}
            />
          ))}
        </div>
      </div>

      {/* Skeleton grid */}
      <div className="relative z-10 w-full flex-1 px-1 sm:px-0 pb-6">
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="overflow-hidden rounded-2xl border border-violet-100/70 bg-white/70 shadow-md backdrop-blur-[2px] motion-reduce:animate-none animate-[fade-in_0.45s_ease-out_both]"
              style={{ animationDelay: `${Math.min(i, 7) * 70}ms` }}
            >
              <div className="relative aspect-square sm:aspect-[3/4] overflow-hidden bg-gradient-to-br from-slate-100/90 via-violet-50/80 to-indigo-50/90">
                <div
                  className="absolute inset-0 w-[200%] -translate-x-1/4 bg-gradient-to-r from-transparent via-white/55 to-transparent motion-reduce:animate-none animate-shimmer"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              </div>
              <div className="space-y-2 border-t border-violet-50/90 p-3">
                <div className="h-3.5 w-4/5 rounded-full bg-gradient-to-r from-slate-200 to-violet-100/90 motion-reduce:animate-none animate-pulse" />
                <div
                  className="h-2.5 w-1/2 rounded-full bg-slate-100 motion-reduce:animate-none animate-pulse"
                  style={{ animationDelay: '180ms' }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AiCreationsLoader;
